import React, { useCallback, useState } from 'react';

import useApi from '@/hooks/useApi';

import { ProgressBar } from '@/components/global/ProgressBar';
import { Screen } from '@/components/Template/Template4/Optin/Screen';
import { ErrorScreen } from '@/components/Template/Template4/Optin/ErrorScreen';

import { optinGame, readGame } from '@/services/requests/Game';
import {
  TOnboardingStep,
  TRecommendation,
} from '@/services/requests/Recommendation/types/template4';

import { getValue } from '@/helpers/GetValueHelper';
import {
  getMemberNumber,
  isLogged,
} from '@/helpers/AuthenticationHelper/SessionTokenHelper';
import { getCTA } from '@/helpers/CtaHelper';

type TProps = {
  data: TRecommendation;
  setOnboarding: (value: boolean) => void;
};

export function Layout1({ data, setOnboarding }: TProps) {
  const [step, setStep] = useState(0);
  const [apiError, setApiError] = useState(false);
  const { ApiGame } = useApi();

  const steps = getValue(
    data,
    'onboarding.steps',
    [],
  ) as Array<TOnboardingStep>;
  const indentifiedStep = getValue(
    data,
    'onboarding.identified_step',
    [],
  ) as Array<TOnboardingStep>;
  const errorStep = getValue(data, 'onboarding.error', null);
  const amountSteps = steps.length;
  const progressBarValue = ((step + 1) / amountSteps) * 100;

  function isLastScreen(index: number) {
    return amountSteps - 1 === index;
  }

  const handlerOnNext = useCallback(() => {
    setStep(prev => prev + 1);
  }, []);

  const handlerOnBack = useCallback(() => {
    setApiError(false);
    setStep(amountSteps - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlerOnSkip = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      setStep(amountSteps - 1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handlerOnSubmit = useCallback(async () => {
    try {
      const idGame = parseInt(
        getValue(data, 'onboarding.id_promotion', '') as string,
        10,
      );
      const memberNumber = getMemberNumber();
      await readGame(ApiGame, idGame);
      await optinGame(ApiGame, memberNumber);

      setOnboarding(false);
    } catch (error) {
      setApiError(true);
    }
  }, [ApiGame, data, setOnboarding]);

  const handlerOnRedirect = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      card: TOnboardingStep,
    ) => {
      event.preventDefault();
      const cta = getCTA(card.cta, 'web');
      const link = getValue(cta, 'link', '') as string;
      window.location.href = link;
    },
    [],
  );

  const renderNotAuthenticated = () => {
    return (
      <>
        {indentifiedStep.map((card, index) => {
          return (
            <Screen
              key={`${index + 1}-screen`}
              data={card}
              isShow={index !== step}
              isLastScreen={isLastScreen(index)}
              onNext={event => handlerOnRedirect(event, card)}
            />
          );
        })}
      </>
    );
  };

  const renderErrorScreen = () => {
    return errorStep && <ErrorScreen data={errorStep} onBack={handlerOnBack} />;
  };

  const renderAuthenticated = () => {
    return (
      <>
        {steps.map((card, index) => (
          <Screen
            key={`${index + 1}-screen`}
            data={card}
            isShow={index !== step}
            isLastScreen={isLastScreen(index)}
            onNext={handlerOnNext}
            onSkip={handlerOnSkip}
            onSubmit={handlerOnSubmit}
          />
        ))}
        <div className="vitrine-template4__box3">
          <ProgressBar
            className="vitrine-template4__progressbar"
            steps={4}
            value={progressBarValue}
          />
        </div>
      </>
    );
  };

  return (
    <div className="vitrine-template4__screen">
      {apiError && renderErrorScreen()}
      {!apiError && isLogged() && renderAuthenticated()}
      {!apiError && !isLogged() && renderNotAuthenticated()}
    </div>
  );
}
